import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import HoloPlacard from "@/components/holo/HoloPlacard";
import { Receipt, Home, Car, CreditCard, PiggyBank, Shield, Repeat } from "lucide-react";
import type { AnyValue } from "@/lib/rowTypes";

interface Expense {
  id: string;
  category: string;
  description: string;
  amount: number;
  source_module: string | null;
}

const CATEGORY_ICONS: Record<string, AnyValue> = {
  housing: Home,
  transportation: Car,
  debt: CreditCard,
  savings: PiggyBank,
  insurance: Shield,
  subscription: Repeat,
};

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function MonthlyExpensesSummary() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Get monthly expenses from DB
      const { data: dbExpenses } = await supabase
        .from("monthly_expenses")
        .select("*")
        .eq("user_id", user.id)
        .eq("active", true);

      // Also derive from liabilities
      const { data: liabilities } = await supabase
        .from("user_liabilities")
        .select("*")
        .eq("user_id", user.id);

      const all: Expense[] = [];

      // DB expenses
      if (dbExpenses) {
        (dbExpenses).forEach(e => {
          all.push({
            id: e.id,
            category: e.category,
            description: e.description,
            amount: e.amount,
            source_module: e.source_module,
          });
        });
      }

      // Liabilities as monthly obligations
      if (liabilities) {
        (liabilities).forEach(l => {
          all.push({
            id: l.id,
            category: l.liability_type === "auto_loan" ? "transportation" : "debt",
            description: `${l.liability_name} Payment`,
            amount: l.monthly_payment_estimate,
            source_module: null,
          });
        });
      }

      // Credit minimum payment
      const { data: credit } = await supabase
        .from("credit_accounts")
        .select("minimum_payment, current_balance, status")
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();

      if (credit && (credit).current_balance > 0) {
        all.push({
          id: "credit-min",
          category: "debt",
          description: "Credit Card Minimum Payment",
          amount: (credit).minimum_payment,
          source_module: null,
        });
      }

      setExpenses(all);
      setLoading(false);
    })();
  }, [user]);

  if (loading) return null;

  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  if (expenses.length === 0 && total === 0) return null;

  return (
    <HoloPlacard className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-bold text-sm text-foreground">Monthly Obligations</h3>
        </div>
        <span className="font-heading font-bold text-foreground">{fmt(total)}<span className="text-xs text-muted-foreground font-normal">/mo</span></span>
      </div>

      {expenses.length > 0 && (
        <div className="space-y-2">
          {expenses.map(expense => {
            const Icon = CATEGORY_ICONS[expense.category] || Receipt;
            return (
              <div key={expense.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground truncate">{expense.description}</span>
                </div>
                <span className="text-foreground font-medium shrink-0 ml-2">{fmt(expense.amount)}</span>
              </div>
            );
          })}
        </div>
      )}

      {expenses.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-2">
          No recurring expenses yet. Complete simulators to see obligations here.
        </p>
      )}
    </HoloPlacard>
  );
}
